// Austria: the vehicle main group (Hauptgruppe) follows from the legal vehicle
// type and the vehicle usage code. Frontend mirror of the backend
// VehicleUtilities.GetVehicleMainGroupAT (GrECo.BFM.DataAccess/Utilities/
// VehicleUtilities_MainGroup_AT.cs); both implement
// "2026-09-04 Mapping main groups.xlsx" - keep the two in sync.
//
// The usage wins over the vehicle type; when none of the listed usages applies
// the main group depends on the vehicle type alone. Trade license plates are
// not handled here (they are always HG9).

import { TaxonomyState } from "store/taxonomy";

// vehicle usages which fix the main group regardless of the vehicle type
const VEHICLE_USAGE_2_MAIN_GROUP_AT: { [usage: string]: string } = {
  "10": "HG6",
  "24": "HG10",
  "27": "HG8",
  "30": "HG7",
  "31": "HG7",
  "40": "HG7",
  "62": "HG7",
  "63": "HG7",
  "64": "HG7",
  "72": "HG7",
  "79": "HG7",
};

// taxi / rental like usages move cars from HG2 to HG3; all other vehicle types
// keep their default main group
const VEHICLE_USAGES_CARS_TO_HG3_AT = new Set(["25", "29"]);
const VEHICLE_TYPES_CARS_TO_HG3_AT = new Set([
  "PASSENGER_CAR",
  "ESTATE_CAR",
  "CAR_FOR_DISABLED_PERSONS",
]);

// default main group per legal vehicle type ("unless one of the vehicle uses
// listed applies")
const VEHICLE_TYPE_2_MAIN_GROUP_AT: { [vehicleType: string]: string } = {
  MOPED: "HG1",
  THREE_WHEELED_MOPED: "HG1",
  MOTORCYCLE: "HG1",
  MOTORCYCLE_WITH_SIDECAR: "HG1",
  THREE_WHEELER_VEHICLE: "HG1",
  PASSENGER_CAR: "HG2",
  ESTATE_CAR: "HG2",
  BUS: "HG4",
  TRUCK: "HG5",
  LIGHT_FOUR_WHEELED_VEHICLE: "HG5",
  HEAVY_FOUR_WHEELED_VEHICLE: "HG5",
  TRACTOR: "HG5",
  ENGINE_CART: "HG5",
  DUMPER: "HG5",
  TRUCK_TRACTOR: "HG5",
  ARTICULATED_VEHICLE: "HG5",
  PERSONNEL_CARRIER: "HG2",
  SELF_PROPELLED_WORK_MASCHINE: "HG7",
  SPECIAL_VEHICLE: "HG5",
  CAR_FOR_DISABLED_PERSONS: "HG2",
  CRAWLER_VEHICLE: "HG5",
  TRAILER: "HG11",
  SEMI_TRAILER: "HG11",
  CENTRE_AXLE_TRAILER: "HG11",
  RIGID_DRAWBAR_TRAILER: "HG11",
  TRAILER_WORK_MASCHINE: "HG11",
  SINGLE_AXLE_TRAILER: "HG11",
};

const VEHICLE_USAGE_TYPE_AT_PREFIX = "AT_";

// Taxonomy codes carry the taxonomy name ("VehicleTypeLegal.PASSENGER_CAR",
// "VehicleUsageType.AT_10"); the mapping works on the part behind the dot.
const taxonomyCodeSuffix = (code: string | null | undefined) => {
  if (!code) return null;
  const separator = code.indexOf(".");
  return separator >= 0 ? code.substring(separator + 1) : code;
};

/**
 * Main group code ("HG1".."HG11") for a legal vehicle type and a vehicle usage,
 * both given as taxonomy codes. Null when the vehicle type is missing. A
 * missing, unknown or non-AT usage falls back to the vehicle type default; a
 * listed usage wins even for a vehicle type the default table does not know
 * (then null without such a usage) - the same as the backend.
 */
export const getVehicleMainGroupAT = (
  vehicleTypeLegalCode: string | null | undefined,
  vehicleUsageTypeCode: string | null | undefined
): string | null => {
  const vehicleType = taxonomyCodeSuffix(vehicleTypeLegalCode);
  if (!vehicleType) return null;

  const usageCode = taxonomyCodeSuffix(vehicleUsageTypeCode)?.toUpperCase();
  const usage = usageCode?.startsWith(VEHICLE_USAGE_TYPE_AT_PREFIX)
    ? usageCode.substring(VEHICLE_USAGE_TYPE_AT_PREFIX.length)
    : null;
  if (usage) {
    const usageMainGroup = VEHICLE_USAGE_2_MAIN_GROUP_AT[usage];
    if (usageMainGroup) return usageMainGroup;
    if (
      VEHICLE_USAGES_CARS_TO_HG3_AT.has(usage) &&
      VEHICLE_TYPES_CARS_TO_HG3_AT.has(vehicleType)
    ) {
      return "HG3";
    }
  }

  return VEHICLE_TYPE_2_MAIN_GROUP_AT[vehicleType] ?? null;
};

/**
 * Same as getVehicleMainGroupAT, but on taxonomy ids as they are stored on the
 * vehicle and used as the form's option values: returns the VehicleMainGroup
 * taxonomy id, or null when it cannot be determined.
 */
export const determineVehicleMainGroupCodeAT = (
  vehicleTypeCode: number | null | undefined,
  carUsageCode: number | null | undefined,
  taxonomy: TaxonomyState
): number | null => {
  const mainGroup = getVehicleMainGroupAT(
    vehicleTypeCode != null
      ? taxonomy.VehicleTypeLegal?.byId[vehicleTypeCode]?.code
      : null,
    carUsageCode != null
      ? taxonomy.VehicleUsageType?.byId[carUsageCode]?.code
      : null
  );
  if (!mainGroup) return null;
  return (
    taxonomy.VehicleMainGroup?.byCode[`VehicleMainGroup.${mainGroup}`]?.id ??
    null
  );
};
